import React from 'react';
import Loader from '../../../../../../Components/Loader';
import 'react-toastify/dist/ReactToastify.css';
import { usePageLevelScriptConfigContext } from '../../../../../../Contexts/PageLevelScriptConfigContext';
import PagelevelScriptConfigGenerator from './PagelevelScriptConfigGenerator';
import useStyles from './styles';
import useCommonStyles from '../../../../../../commonStyles';
import { PAGE_LEVEL_SCRIPT_CONFIG_MODES } from '../../../../../../core/constants';
import { Paper, Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import SiteWisePageLevelConfigView from './Components/SiteWisePageLevelConfigView';

/**
 * PageLevelScriptConfig is a component that acts as the main interface for managing
 * page-level script configs within the dashboard. This component dynamically renders
 * different views based on the current mode of operation, which includes error handling,
 * loading states, creation of new configs, and updating existing configs.
 *
 * Key functionalities include:
 * - Displaying a loading state while data is being fetched.
 * - Handling and displaying errors in config fetching or processing.
 * - Allowing the creation of new page-level script configs.
 * - Providing an interface for updating existing configs.
 * - Rendering a site-wise overview of all page-level configs.
 *
 * This component relies on PageLevelScriptConfigContext for state management
 */
const PageLevelScriptConfig = () => {
	const {
		siteId,
		pageLevelScriptConfig,
		pageLevelScriptConfigMode,
		handlePageLevelScriptConfigSinglePageSync,
		handlePageLevelScriptConfigBulkPageSync,
		setPageLevelScriptConfigMode,
		setPageLevelScriptConfigPage
	} = usePageLevelScriptConfigContext();

	let component;

	switch (pageLevelScriptConfigMode) {
		case PAGE_LEVEL_SCRIPT_CONFIG_MODES.PAGE_CONFIG_ERROR:
			component = <ErrorComponent />;
			break;

		case PAGE_LEVEL_SCRIPT_CONFIG_MODES.PAGE_CONFIG_LOADING:
			component = <Loader />;
			break;

		case PAGE_LEVEL_SCRIPT_CONFIG_MODES.PAGE_CONFIG_NOT_FOUND:
			component = <CreateConfig />;
			break;

		case PAGE_LEVEL_SCRIPT_CONFIG_MODES.CREATE_PAGE_CONFIG:
		case PAGE_LEVEL_SCRIPT_CONFIG_MODES.UPDATE_PAGE_CONFIG:
			component = <PagelevelScriptConfigGenerator />;
			break;

		case PAGE_LEVEL_SCRIPT_CONFIG_MODES.SITE_LEVEL_CREATE_PAGE_CONFIGS:
		case PAGE_LEVEL_SCRIPT_CONFIG_MODES.SITE_LEVEL_UPDATE_PAGE_CONFIGS:
			component = pageLevelScriptConfig ? (
				<SiteWisePageLevelConfigView
					pageLevelScriptConfigMode={pageLevelScriptConfigMode}
					pageLevelScriptConfig={pageLevelScriptConfig}
					handlePageLevelScriptConfigSinglePageSync={handlePageLevelScriptConfigSinglePageSync}
					handlePageLevelScriptConfigBulkPageSync={handlePageLevelScriptConfigBulkPageSync}
					siteId={siteId}
					setPageLevelScriptConfigMode={setPageLevelScriptConfigMode}
					setPageLevelScriptConfigPage={setPageLevelScriptConfigPage}
				/>
			) : null;
			break;

		default:
			component = <ErrorComponent />;
	}

	return component;
};

/**
 * ErrorComponent: Displays a message when an error occurs.
 * Rendered when:
 * - There's an error in fetching or processing page-level script configs.
 * - The current mode falls into an undefined case.
 */
const ErrorComponent = () => {
	const classes = useStyles();
	const commonStyleClasses = useCommonStyles();
	<Paper className={classes.containerWrapper}>
		<Typography className={commonStyleClasses.heading} fontWeight={600} variant="h3">
			Some error has occurred
		</Typography>
	</Paper>;
};

/**
 * CreateConfig: Provides a UI for creating a new page-level script config.
 * Rendered when:
 * - A page-level script config is not found for the current context.
 */
const CreateConfig = () => {
	const classes = useStyles();
	const commonStyleClasses = useCommonStyles();
	const { setPageLevelScriptConfigMode } = usePageLevelScriptConfigContext();

	const handleCreateConfig = () => {
		setPageLevelScriptConfigMode(PAGE_LEVEL_SCRIPT_CONFIG_MODES.SITE_LEVEL_CREATE_PAGE_CONFIGS);
	};

	return (
		<Paper className={classes.containerWrapper}>
			<Typography className={commonStyleClasses.heading} fontWeight={600} variant="h3">
				Page level config doesn't exist already
			</Typography>
			<Button variant="outlined" size="large" onClick={() => handleCreateConfig()}>
				Create Config
			</Button>
		</Paper>
	);
};

export default PageLevelScriptConfig;
